import React, { useState, useEffect, useCallback, useRef } from 'react';
import { AlertTriangle, RefreshCw, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  Button, SectionHeading, ICON_PX,
} from '../../ui';
import { useEventStream } from '../../../hooks/useEventStream';
import { useAuth } from '../../../contexts/AuthContext';
import { apiFetch } from '../../../services/apiClient';

const PHASE_LABELS = {
  clearing: 'Clearing tables',
  migrating: 'Applying migrations',
  seeding: 'Seeding data',
  verifying: 'Verifying integrity',
};

function phaseIcon(phase, currentPhase, done) {
  if (done) return <CheckCircle size={14} className="text-green shrink-0" />;
  if (phase === currentPhase) return <Loader2 size={14} className="text-aqua shrink-0 animate-spin" />;
  return <div className="w-3.5 h-3.5 rounded-full border border-border-card shrink-0" />;
}

export default function DemoSetupPage() {
  const { getToken } = useAuth();
  const { subscribe } = useEventStream();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Seed state
  const [seedStatus, setSeedStatus] = useState('idle'); // idle | running | completed | failed
  const [percent, setPercent] = useState(0);
  const [currentPhase, setCurrentPhase] = useState(null);
  const [currentStep, setCurrentStep] = useState(null);
  const [completedPhases, setCompletedPhases] = useState([]);
  const [error, setError] = useState(null);
  const [lastCompleted, setLastCompleted] = useState(null);
  const [elapsed, setElapsed] = useState(null);

  const prevPhaseRef = useRef(null);

  // Fetch initial status on mount
  useEffect(() => {
    apiFetch('/api/admin/seed/status', {}, getToken)
      .then((data) => {
        if (data.isRunning) setSeedStatus('running');
        if (data.lastCompleted) setLastCompleted(data.lastCompleted);
        if (data.lastError) {
          setError(data.lastError);
          setSeedStatus('failed');
        }
      })
      .catch(() => { /* endpoint may not exist if flag off */ });
  }, [getToken]);

  // SSE subscriptions
  useEffect(() => {
    const unsubs = [
      subscribe('seed.progress', (data) => {
        setSeedStatus('running');
        setPercent(data.percent ?? 0);
        setCurrentStep(data.step ?? null);

        if (data.phase && data.phase !== prevPhaseRef.current) {
          if (prevPhaseRef.current) {
            setCompletedPhases((prev) =>
              prev.includes(prevPhaseRef.current) ? prev : [...prev, prevPhaseRef.current],
            );
          }
          prevPhaseRef.current = data.phase;
        }
        setCurrentPhase(data.phase ?? null);
      }),
      subscribe('seed.completed', (data) => {
        // Mark ALL phases as completed
        setCompletedPhases(['clearing', 'migrating', 'seeding', 'verifying']);
        setSeedStatus('completed');
        setPercent(100);
        setCurrentPhase(null);
        setCurrentStep(null);
        setError(null);
        setLastCompleted(data.completedAt ?? new Date().toISOString());
        if (data.startedAt) {
          setElapsed(Math.round((new Date(data.completedAt).getTime() - new Date(data.startedAt).getTime()) / 1000));
        }
        prevPhaseRef.current = null;
      }),
      subscribe('seed.failed', (data) => {
        setSeedStatus('failed');
        setError(data.error ?? 'Unknown error');
        setCurrentPhase(null);
        setCurrentStep(null);
        prevPhaseRef.current = null;
      }),
    ];
    return () => unsubs.forEach((u) => u());
  }, [subscribe]);

  const handleTrigger = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    setSeedStatus('running');
    setPercent(0);
    setCompletedPhases([]);
    setCurrentPhase(null);
    setCurrentStep(null);
    setElapsed(null);
    prevPhaseRef.current = null;

    try {
      await apiFetch('/api/admin/seed', { method: 'POST' }, getToken);
      setDialogOpen(false);
    } catch (err) {
      if (err.message?.includes('409')) {
        setError('A seed is already in progress');
        setSeedStatus('running');
      } else {
        setError(err.message || 'Failed to trigger seed');
        setSeedStatus('failed');
      }
    } finally {
      setSubmitting(false);
    }
  }, [getToken]);

  const isRunning = seedStatus === 'running';
  const phases = ['clearing', 'migrating', 'seeding', 'verifying'];

  return (
    <div className="dashboard-container">
      <SectionHeading info="Reset the nexonurse tenant demo data to its default state. All existing data will be deleted and re-seeded.">
        Demo Data Reset
      </SectionHeading>

      {/* Status card */}
      <div className="bg-nn-white rounded-card-lg shadow-card p-6 space-y-5">
        {/* Status header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {seedStatus === 'idle' && (
              <span className="text-sm text-text-dark-secondary">Ready to reset</span>
            )}
            {seedStatus === 'running' && (
              <>
                <Loader2 size={16} className="text-aqua animate-spin" />
                <span className="text-sm text-aqua font-medium">Reset in progress…</span>
              </>
            )}
            {seedStatus === 'completed' && (
              <>
                <CheckCircle size={16} className="text-green" />
                <span className="text-sm text-green font-medium">
                  Reset complete{elapsed != null ? ` (${elapsed}s)` : ''}
                </span>
              </>
            )}
            {seedStatus === 'failed' && (
              <>
                <XCircle size={16} className="text-red-critical" />
                <span className="text-sm text-red-critical font-medium">Reset failed</span>
              </>
            )}
          </div>
          {lastCompleted && (
            <span className="text-xs text-text-dark-secondary">
              Last reset: {new Date(lastCompleted).toLocaleString()}
            </span>
          )}
        </div>

        {/* Progress bar */}
        {isRunning && (
          <div className="space-y-2">
            <div className="h-2 bg-navy-light/20 rounded-full overflow-hidden">
              <div
                className="h-full bg-aqua rounded-full transition-all duration-300"
                style={{ width: `${percent}%` }}
              />
            </div>
            <div className="text-xs text-text-dark-secondary">
              {percent}% — {currentStep || 'Starting…'}
            </div>
          </div>
        )}

        {/* Phase steps */}
        {(isRunning || seedStatus === 'completed') && (
          <div className="space-y-2">
            {phases.map((phase) => (
              <div key={phase} className="flex items-center gap-2 text-sm min-w-0">
                {phaseIcon(phase, currentPhase, completedPhases.includes(phase))}
                <span className={`truncate ${
                  completedPhases.includes(phase) ? 'text-text-dark' :
                  phase === currentPhase ? 'text-aqua font-medium' :
                  'text-text-dark-secondary'
                }`}>
                  {PHASE_LABELS[phase]}
                  {phase === currentPhase && currentStep ? ` — ${currentStep}` : ''}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="flex items-start gap-2 rounded-xl border border-red-critical/30 bg-red-bg p-3">
            <AlertTriangle size={ICON_PX.md} className="text-red-critical shrink-0 mt-0.5" />
            <div className="text-sm text-text-dark break-words min-w-0">{error}</div>
          </div>
        )}

        {/* Action button */}
        <div>
          <Button
            variant="danger"
            onClick={() => setDialogOpen(true)}
            disabled={isRunning}
          >
            <RefreshCw size={14} className={isRunning ? 'animate-spin' : ''} />
            {isRunning ? 'Reset in progress…' : 'Reset Demo Data'}
          </Button>
        </div>
      </div>

      {/* Confirmation dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { if (!o && !submitting) setDialogOpen(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reset demo data?</DialogTitle>
          </DialogHeader>

          <div className="px-6 py-4 space-y-3">
            <div className="flex items-start gap-2 rounded-xl border border-red-critical/30 bg-red-bg p-3">
              <AlertTriangle size={ICON_PX.md} className="text-red-critical shrink-0 mt-0.5" />
              <div className="text-sm text-text-dark">
                This will <strong>delete all data</strong> in the nexonurse tenant and re-seed from scratch.
                All users will see a reset-in-progress notification. This cannot be undone.
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleTrigger} disabled={submitting}>
              {submitting ? 'Starting…' : 'Reset Data'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
